import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Circle, CircleMarker, Popup } from "react-leaflet";
import client from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { RefreshCw, Loader2, Crosshair, ShieldAlert, Flame, UserPlus, X, FileText } from "lucide-react";

const LEVEL = { critical: "#DC2626", high: "#EA580C", moderate: "#CA8A04" };
const RANGES = [[30, "30d"], [60, "60d"], [90, "90d"], [180, "180d"]];
const sel = "bg-[#0d0e11] border border-[#2C2D35] px-2 py-1.5 text-xs outline-none focus:border-[#0EA5E9] font-mono text-[#F3F4F6]";

export default function CrimeIntel() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);
  const [crimeType, setCrimeType] = useState("");
  const [heatmap, setHeatmap] = useState(false);
  const [units, setUnits] = useState([]);
  const [assignments, setAssignments] = useState([]);

  const canAssign = ["supervisor", "watch_commander", "admin", "police_dispatcher", "fire_dispatcher", "ems_dispatcher"].includes(user?.role);

  const load = async (refresh = false) => {
    setLoading(true);
    try {
      const params = { days };
      if (crimeType) params.crime_type = crimeType;
      if (refresh) params.refresh = true;
      const { data } = await client.get("/crime/baltimore", { params });
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load Baltimore crime data"); }
    finally { setLoading(false); }
  };
  const loadUnitsAssign = async () => {
    try {
      const [u, a] = await Promise.all([client.get("/units"), client.get("/patrol/assignments")]);
      setUnits(u.data || []); setAssignments(a.data || []);
    } catch { /* noop */ }
  };
  const downloadBriefing = async (unitId) => {
    try {
      const { data } = await client.get(`/patrol/briefing/${encodeURIComponent(unitId)}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `briefing_${unitId.replace(/ /g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`Roll-call briefing generated — ${unitId}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate briefing"); }
  };
  useEffect(() => { load(); loadUnitsAssign(); }, []); // eslint-disable-line
  useEffect(() => { load(); }, [days, crimeType]); // eslint-disable-line

  const assign = async (zone, unitId) => {
    if (!unitId) return;
    try {
      await client.post("/patrol/assign", { unit_id: unitId, zone_rank: zone.rank, lat: zone.lat, lng: zone.lng, level: zone.level, recommendation: zone.recommendation });
      toast.success(`${unitId} assigned to Patrol Zone #${zone.rank}`);
      loadUnitsAssign();
    } catch (e) { toast.error(e.response?.data?.detail || "Assign failed"); }
  };
  const clearAssign = async (aid, unitId) => {
    try { await client.delete(`/patrol/assignments/${aid}`); toast.success(`${unitId} cleared from patrol`); loadUnitsAssign(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const center = data?.center || [39.2904, -76.6122];
  const availUnits = units.filter((u) => u.status === "available" || u.status === "directed_patrol");
  const zoneAssign = (rank) => assignments.filter((a) => a.zone_rank === rank);

  return (
    <Layout>
      <PageHeader title="Crime Intelligence & Smart Patrol" sub="Open Baltimore · Part-1 victim-based crime · density-driven patrol zones" />
      <div className="flex-1 overflow-hidden flex flex-col p-1">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <select data-testid="crime-type-filter" value={crimeType} onChange={(e) => setCrimeType(e.target.value)} className={sel}>
            <option value="">All crime types</option>
            {(data?.available_types || []).map((t) => <option key={t.type} value={t.type}>{t.type} ({t.count})</option>)}
          </select>
          <select data-testid="crime-range-filter" value={days} onChange={(e) => setDays(Number(e.target.value))} className={sel}>
            {RANGES.map(([d, l]) => <option key={d} value={d}>Last {l}</option>)}
          </select>
          <button data-testid="crime-heatmap-toggle" onClick={() => setHeatmap((h) => !h)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-head border ${heatmap ? "bg-[#DC2626] text-white border-[#DC2626]" : "bg-[#1E1F24] text-[#9CA3AF] border-[#2C2D35]"}`}>
            <Flame size={13} /> Heatmap
          </button>
          {data && <div className="font-mono text-[11px] text-[#9CA3AF]" data-testid="crime-window">{data.window?.from} → {data.window?.to} · {data.total} crimes · {data.hotspots?.length} hotspots</div>}
          <button data-testid="crime-refresh" onClick={() => load(true)} disabled={loading}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-[#1E1F24] border border-[#2C2D35] hover:border-[#0EA5E9] text-xs font-head disabled:opacity-50">
            {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Refresh
          </button>
        </div>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-3 min-h-0">
          <div className="lg:col-span-2 border border-[#2C2D35] min-h-[400px]" data-testid="crime-map">
            <MapContainer center={center} zoom={12} style={{ height: "100%", width: "100%", minHeight: 400 }}>
              <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}" attribution="&copy; Esri &mdash; Esri, HERE, Garmin, OpenStreetMap contributors" />
              {heatmap
                ? (data?.points || []).map((p, i) => (
                    <CircleMarker key={"p" + i} center={[p.lat, p.lng]} radius={5}
                      pathOptions={{ color: "#DC2626", fillColor: "#DC2626", fillOpacity: 0.18, weight: 0, opacity: 0 }} />
                  ))
                : (data?.hotspots || []).map((h, i) => (
                    <Circle key={"h" + i} center={[h.lat, h.lng]} radius={250 + h.count * 6}
                      pathOptions={{ color: LEVEL[h.level], fillColor: LEVEL[h.level], fillOpacity: 0.25, weight: 1 }}>
                      <Popup><div className="font-mono text-xs"><b>{h.count}</b> crimes · {h.level.toUpperCase()}<br />{(h.top_types || []).map((t) => `${t.type} (${t.count})`).join(", ")}</div></Popup>
                    </Circle>
                  ))}
              {(data?.patrol_zones || []).map((z, i) => (
                <CircleMarker key={"z" + i} center={[z.lat, z.lng]} radius={11}
                  pathOptions={{ color: "#fff", fillColor: LEVEL[z.level], fillOpacity: 1, weight: 2 }}>
                  <Popup><div className="font-mono text-xs"><b>Patrol Zone #{z.rank}</b><br />{z.recommendation}</div></Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>

          <div className="border border-[#2C2D35] flex flex-col min-h-0">
            <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold flex items-center gap-2 text-[#F59E0B]">
              <Crosshair size={14} /> SMART PATROL ZONES
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-2" data-testid="patrol-zones">
              {loading && <div className="flex items-center gap-2 text-xs font-mono text-[#9CA3AF] p-2"><Loader2 size={13} className="animate-spin" /> Pulling Baltimore crime data…</div>}
              {!loading && (data?.patrol_zones || []).length === 0 && <div className="text-xs font-mono text-[#9CA3AF] p-2">No patrol zones for this filter.</div>}
              {(data?.patrol_zones || []).map((z) => {
                const assigned = zoneAssign(z.rank);
                return (
                  <div key={z.rank} data-testid={`patrol-zone-${z.rank}`} className="border p-2.5 bg-[#141518]" style={{ borderColor: LEVEL[z.level] + "66" }}>
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full flex items-center justify-center font-head font-bold text-[11px] text-white" style={{ background: LEVEL[z.level] }}>{z.rank}</span>
                      <span className="font-mono text-[10px] font-bold uppercase" style={{ color: LEVEL[z.level] }}>{z.level}</span>
                      <span className="ml-auto font-mono text-[10px] text-[#9CA3AF]">{z.count} crimes</span>
                    </div>
                    <div className="mt-1 text-[11px] text-[#D1D5DB] flex items-start gap-1.5"><ShieldAlert size={12} className="mt-0.5 shrink-0" style={{ color: LEVEL[z.level] }} /> {z.recommendation}</div>
                    {assigned.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`zone-assigned-${z.rank}`}>
                        {assigned.map((a) => (
                          <span key={a.id} className="flex items-center gap-1 font-mono text-[10px] px-1.5 py-0.5 bg-[#16A34A]/20 text-[#4ADE80] border border-[#16A34A]">
                            {a.unit_id}
                            <button data-testid={`brief-${a.unit_id}`} onClick={() => downloadBriefing(a.unit_id)}
                              title="Roll-call briefing sheet" className="hover:text-white"><FileText size={10} /></button>
                            {canAssign && <button data-testid={`clear-assign-${a.unit_id}`} onClick={() => clearAssign(a.id, a.unit_id)} className="hover:text-white"><X size={10} /></button>}
                          </span>
                        ))}
                      </div>
                    )}
                    {canAssign && (
                      <div className="mt-2 flex gap-1.5">
                        <select data-testid={`assign-select-${z.rank}`} defaultValue="" className={sel + " flex-1"} id={`asg-${z.rank}`}>
                          <option value="">Assign unit…</option>
                          {availUnits.map((u) => <option key={u.unit_id} value={u.unit_id}>{u.unit_id} ({u.type}{u.status === "directed_patrol" ? " · on patrol" : ""})</option>)}
                        </select>
                        <button data-testid={`assign-btn-${z.rank}`} onClick={() => { const s = document.getElementById(`asg-${z.rank}`); assign(z, s.value); s.value = ""; }}
                          className="flex items-center gap-1 px-2.5 py-1.5 bg-[#0EA5E9] text-black font-head text-xs font-semibold"><UserPlus size={12} /> Assign</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {data && <div className="px-2 py-1.5 border-t border-[#2C2D35] font-mono text-[9px] text-[#6B7280]">{data.source}</div>}
          </div>
        </div>
      </div>
    </Layout>
  );
}
